import React from 'react';
import { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { AUTHORIZED_EMAILS, AUTH_CONFIG } from '../config/auth';
import { generateAuthToken } from '../utils/authToken';
import logoApiki from '../assets/logo-apiki.png';

export default function AuthGuard({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showExpirationWarning, setShowExpirationWarning] = useState(false);
  const [expirationTime, setExpirationTime] = useState(null);

  useEffect(() => {
    // Verificar se há dados de usuário salvos no localStorage
    const savedUser = localStorage.getItem(AUTH_CONFIG.userStorageKey);
    const savedToken = localStorage.getItem('authToken');
    
    if (savedUser && savedToken) {
      try {
        const userData = JSON.parse(savedUser);
        
        // Verificar se a sessão não expirou
        const sessionExpiration = new Date(userData.sessionExpiration);
        setExpirationTime(sessionExpiration);
        if (sessionExpiration > new Date()) {
          setUser(userData);
          setAuthToken(savedToken);
          setIsAuthenticated(true);
          setIsAuthorized(AUTHORIZED_EMAILS.includes(userData.email));
        } else {
          // Sessão expirada, remover dados
          localStorage.removeItem(AUTH_CONFIG.userStorageKey);
          localStorage.removeItem('authToken');
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
        localStorage.removeItem(AUTH_CONFIG.userStorageKey);
        localStorage.removeItem('authToken');
      }
    }
    setIsLoading(false);
  }, []);

  // Aviso de expiração iminente e refresh automático
  useEffect(() => {
    if (!expirationTime) return;
    const now = new Date();
    const timeToExpiration = expirationTime - now;
    const warningTime = 5 * 60 * 1000; // 5 minutos
    const refreshTime = 1 * 60 * 1000; // 1 minuto

    let warningTimeout, refreshTimeout;

    if (timeToExpiration > warningTime) {
      warningTimeout = setTimeout(() => {
        setShowExpirationWarning(true);
      }, timeToExpiration - warningTime);
    } else if (timeToExpiration > 0) {
      setShowExpirationWarning(true);
    }

    if (timeToExpiration > refreshTime) {
      refreshTimeout = setTimeout(() => {
        window.location.reload();
      }, timeToExpiration - refreshTime);
    } else if (timeToExpiration > 0) {
      // Se já está no período crítico, faz refresh logo antes de expirar
      refreshTimeout = setTimeout(() => {
        window.location.reload();
      }, timeToExpiration);
    }

    return () => {
      if (warningTimeout) clearTimeout(warningTimeout);
      if (refreshTimeout) clearTimeout(refreshTimeout);
    };
  }, [expirationTime]);

  const handleLoginSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      
      // Verificar se o email está na lista de autorizados
      const isEmailAuthorized = AUTHORIZED_EMAILS.includes(decoded.email);
      
      if (isEmailAuthorized) {
        // Adicionar data de expiração da sessão
        const sessionExpiration = new Date();
        sessionExpiration.setHours(sessionExpiration.getHours() + AUTH_CONFIG.sessionExpirationHours);
        
        const userData = {
          ...decoded,
          sessionExpiration: sessionExpiration.toISOString()
        };
        
        // Gerar token de autenticação via backend
        const token = await generateAuthToken(userData);
        setUser(userData);
        setAuthToken(token);
        setIsAuthenticated(true);
        setIsAuthorized(true);
        
        // Salvar dados do usuário e token no localStorage
        localStorage.setItem(AUTH_CONFIG.userStorageKey, JSON.stringify(userData));
        localStorage.setItem('authToken', token);

      } else {
        alert(`Acesso negado. O email ${decoded.email} não está autorizado.`);
        console.error('Email não autorizado:', decoded.email);
      }
    } catch (error) {
      console.error('Erro ao processar login:', error);
      alert('Erro ao processar login. Tente novamente.');
    }
  };

  const handleLoginError = () => {
    console.error('Erro no login do Google');
    alert('Erro no login do Google. Tente novamente.');
  };

  const handleLogout = () => {
    setUser(null);
    setAuthToken(null);
    setIsAuthenticated(false);
    setIsAuthorized(false);
    localStorage.removeItem(AUTH_CONFIG.userStorageKey);
    localStorage.removeItem('authToken');
  };

  // Mostrar loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <div className="app-container">
        <div className="form-wrapper">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">Verificando autenticação...</p>
          </div>
        </div>
      </div>
    );
  }

  // Se não está autenticado, mostrar tela de login
  if (!isAuthenticated) {
    return (
      <div className="app-container">
        <div className="form-wrapper">
          <div className="auth-container">
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ background: '#000', borderRadius: 10, padding: 12, display: 'inline-block' }}>
                <img src={logoApiki} alt="Logo Apiki" style={{ display: 'block', maxWidth: 60, maxHeight: 60, objectFit: 'contain' }} />
              </div>
            </div>
            <h1 className="form-title">Acesso Restrito</h1>
            <p className="form-subtitle">
              Faça login com sua conta Google para acessar os formulários
            </p>
            
            {/* <div className="auth-info">
              <p className="auth-text">
                <strong>Emails autorizados:</strong>
              </p>
              <ul className="auth-emails">
                {AUTHORIZED_EMAILS.map((email) => (
                  <li key={email} className="auth-email">{email}</li>
                ))}
              </ul>
            </div> */}

            <div className="google-login-container">
              <GoogleLogin
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
                useOneTap
                theme="filled_blue"
                size="large"
                text="signin_with"
                shape="rectangular"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Se está autenticado mas não autorizado
  if (!isAuthorized) {
    return (
      <div className="app-container">
        <div className="form-wrapper">
          <div className="auth-container">
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ background: '#000', borderRadius: 10, padding: 12, display: 'inline-block' }}>
                <img src={logoApiki} alt="Logo Apiki" style={{ display: 'block', maxWidth: 60, maxHeight: 60, objectFit: 'contain' }} />
              </div>
            </div>
            <h1 className="form-title">Acesso Negado</h1>
            <p className="form-subtitle">
              O email {user?.email} não está autorizado para acessar este sistema.
            </p>
            
            <div className="auth-actions">
              <button 
                onClick={handleLogout}
                className="submit-button"
              >
                Tentar com outra conta
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Se está autenticado e autorizado, mostrar o conteúdo
  return (
    <div>
      {/* Header com informações do usuário */}
      <div className="user-header">
        <div className="user-info">
          <span className="user-name">Olá, {user?.name}</span>
          <span className="user-email">({user?.email})</span>
        </div>
        <button 
          onClick={handleLogout}
          className="logout-button"
        >
          Sair
        </button>
      </div>
      {/* Aviso de expiração iminente */}
      {showExpirationWarning && (
        <div className="expiration-warning" style={{background: '#fff3cd', color: '#856404', padding: 12, borderRadius: 6, margin: '16px 0', border: '1px solid #ffeeba'}}>
          <strong>Atenção:</strong> Sua sessão irá expirar em breve. Salve seu trabalho! A página será recarregada automaticamente para renovar sua sessão.
        </div>
      )}
      {/* Conteúdo principal - passar o token como prop */}
      {React.cloneElement(children, { authToken })}
    </div>
  );
} 